/*
 *	Service to interact with the backend API for fetching and updating tiers.
 *	Provides functions to get tiers, create new tiers, update existing tiers, and delete tiers.
 */

import { apiResource, SelectItem } from '../../../common/services/apiClient';
import { getApiBaseUrl } from 'common/services/apiBaseUrl';
import { apiFetch } from 'common/services/apiFetch';
import { TiersData } from '../types/types';

const apiBaseUrl = getApiBaseUrl();
export const API_URL = apiBaseUrl + '/v2';

const baseApi = apiResource<TiersData>('tiers');

async function requestSelect(
	path: string,
	token: string
): Promise<SelectItem[]> {
	const res = await apiFetch(`${API_URL}${path}`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
	});
	if (!res.ok) {
		throw new Error(`Failed to GET ${path}`);
	}
	return res.json();
}

export const tiersApi = {
	...baseApi,
	listSelectClients: (token: string) =>
		requestSelect('/tiers/select/clients', token),
	listSelectSuppliers: (token: string) =>
		requestSelect('/tiers/select/suppliers', token),
};

// Fetch all tiers
export const getTiers = async (token: string): Promise<TiersData[]> => {
	const res = await apiFetch(`${API_URL}/tiers`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
	});
	if (!res.ok) {
		throw new Error('Failed to fetch tiers');
	}
	return res.json();
};

// Fetch a single tier by ID
export const getTiersById = async (
	id: number,
	token: string
): Promise<TiersData> => {
	const res = await apiFetch(`${API_URL}/tiers/${id}`, {
		method: 'GET',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
	});
	if (!res.ok) {
		throw new Error('Failed to fetch tiers');
	}
	return res.json();
};

// Edit an existing tiers
export const updateTiers = async (
	id: number,
	tiers: Partial<TiersData>,
	token: string
): Promise<TiersData> => {
	const res = await apiFetch(`${API_URL}/tiers/${id}`, {
		method: 'PATCH',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(tiers),
	});
	if (!res.ok) {
		throw new Error('Failed to update tiers');
	}
	return res.json();
};

// Create a new tiers
export const createTiers = async (
	tiers: Partial<TiersData>,
	token: string
): Promise<TiersData> => {
	const res = await apiFetch(`${API_URL}/tiers`, {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(tiers),
	});
	if (!res.ok) {
		throw new Error('Failed to create tiers');
	}
	return res.json();
};

// Delete a tiers by ID
export const deleteTiers = async (id: number, token: string): Promise<void> => {
	const res = await apiFetch(`${API_URL}/tiers/${id}`, {
		method: 'DELETE',
		headers: {
			Authorization: `Bearer ${token}`,
			'Content-Type': 'application/json',
		},
	});
	if (!res.ok) {
		throw new Error('Failed to delete tiers');
	}
};
